// LoginPage - User login with email/password
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Anchor,
  Divider,
  Center,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconMail, IconLock, IconBrandGoogle } from '@tabler/icons-react';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';

export const LoginPage = () => {
  const { login, isLoggingIn } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email address';
        return null;
      },
      password: (value) => {
        if (!value) return 'Password is required';
        return null;
      },
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      await login(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Center mb="xl">
        <Title order={1} style={{ color: '#2196f3' }}>
          CarLeads
        </Title>
      </Center>

      <Title ta="center" order={2} mb="md">
        Welcome back
      </Title>

      <Text c="dimmed" size="sm" ta="center" mb="xl">
        Sign in to your account to continue
      </Text>

      <Paper withBorder shadow="md" p={30} radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Email"
              placeholder="your@email.com"
              leftSection={<IconMail size={16} />}
              required
              {...form.getInputProps('email')}
              disabled={isSubmitting || isLoggingIn}
            />

            <PasswordInput
              label="Password"
              placeholder="Your password"
              leftSection={<IconLock size={16} />}
              required
              {...form.getInputProps('password')}
              disabled={isSubmitting || isLoggingIn}
            />

            <Anchor
              component={Link}
              to={ROUTES.FORGOT_PASSWORD}
              size="sm"
              ta="right"
            >
              Forgot password?
            </Anchor>

            <Button
              type="submit"
              fullWidth
              loading={isSubmitting || isLoggingIn}
              size="md"
            >
              Sign in
            </Button>

            <Divider label="or continue with" labelPosition="center" />

            <Button
              variant="default"
              leftSection={<IconBrandGoogle size={16} />}
              fullWidth
              disabled
            >
              Google (Coming soon)
            </Button>

            <Text c="dimmed" size="sm" ta="center">
              Don't have an account?{' '}
              <Anchor component={Link} to={ROUTES.REGISTER} fw={500}>
                Sign up
              </Anchor>
            </Text>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};
